import React, { useState, useEffect } from 'react';
import { Plane, Plus, Search, Calendar, MapPin, Download, Edit2, Trash2, Clock, ArrowRight, X, QrCode, Pencil, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { travelAPI } from '../services/api';
import FlightTracker from '../components/FlightTracker';
import QRCode from 'qrcode';
import { toast } from 'sonner';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const Travel = () => {
  const [filter, setFilter] = useState('all');
  const [trips, setTrips] = useState([]);
  const [flightCards, setFlightCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isBoardingPassOpen, setIsBoardingPassOpen] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [formData, setFormData] = useState({
    destination: '',
    start_date: '',
    end_date: '',
    purpose: '',
    status: 'planned',
    flight_details: '',
    hotel_details: '',
    notes: ''
  });

  // Default travel categories
  const defaultTravelCategories = [
    { id: 'all', name: 'All Trips', color: 'bg-white', icon: '📋' },
    { id: 'family', name: 'Family', color: 'bg-pink-500', icon: '👨‍👩‍👧‍👦' },
    { id: 'business', name: 'Business', color: 'bg-blue-500', icon: '💼' },
    { id: 'vacation', name: 'Vacation', color: 'bg-green-500', icon: '🏖️' },
    { id: 'personal', name: 'Personal', color: 'bg-purple-500', icon: '👤' }
  ];
  
  // Load custom travel categories from localStorage
  const [customTravelCategories, setCustomTravelCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('travel_categories');
      return saved ? JSON.parse(saved) : defaultTravelCategories;
    } catch { return defaultTravelCategories; }
  });
  
  // Editable category state
  const [showTravelCategoryModal, setShowTravelCategoryModal] = useState(false);
  const [newTravelCategoryName, setNewTravelCategoryName] = useState('');
  const [newTravelCategoryIcon, setNewTravelCategoryIcon] = useState('✈️');
  const [newTravelCategoryColor, setNewTravelCategoryColor] = useState('bg-blue-500');
  const [editingTravelCategory, setEditingTravelCategory] = useState(null);
  const [editTravelCategoryValue, setEditTravelCategoryValue] = useState('');
  
  // Available icons and colors
  const travelCategoryIcons = ['📋', '👨‍👩‍👧‍👦', '💼', '🏖️', '👤', '✈️', '🌍', '🏨', '🚗', '🚂', '🚢', '🎒', '🏔️', '🏝️', '🎡', '🎭', '⛷️', '🏕️', '🗺️', '📸'];
  const travelCategoryColors = ['bg-white', 'bg-pink-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-cyan-500', 'bg-red-500', 'bg-yellow-500', 'bg-indigo-500'];
  
  // Category management functions
  const addTravelCategory = () => {
    if (!newTravelCategoryName.trim()) return;
    const categoryId = newTravelCategoryName.toLowerCase().replace(/\s+/g, '-');
    if (customTravelCategories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    const newCategory = { id: categoryId, name: newTravelCategoryName, color: newTravelCategoryColor, icon: newTravelCategoryIcon };
    const updated = [...customTravelCategories, newCategory];
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    setNewTravelCategoryName('');
    setNewTravelCategoryIcon('✈️');
    setNewTravelCategoryColor('bg-blue-500');
    toast.success(`Added "${newTravelCategoryName}" category`);
  };
  
  const updateTravelCategory = (oldId, newName) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    const updated = customTravelCategories.map(c => c.id === oldId ? { ...c, id: newId, name: newName } : c);
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    if (filter === oldId) setFilter(newId);
    setEditingTravelCategory(null);
    setEditTravelCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteTravelCategory = (categoryId) => {
    if (['all', 'business', 'personal'].includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    const updated = customTravelCategories.filter(c => c.id !== categoryId);
    setCustomTravelCategories(updated);
    localStorage.setItem('travel_categories', JSON.stringify(updated));
    if (filter === categoryId) setFilter('all');
    toast.success('Category deleted');
  };

  // Use custom categories
  const categories = customTravelCategories;

  useEffect(() => {
    fetchTravelEvents();
  }, []);

  // Fetch travel events from calendar
  const fetchTravelEvents = async () => {
    try {
      setLoading(true);
      
      // Get events for the next 6 months
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await fetch(`${API_URL}/api/outlook-calendar/desktop-events?start_date=${startDate}&end_date=${endDate}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && Array.isArray(data.events)) {
          // Filter for travel-related events
          const travelEvents = data.events.filter(event => {
            const subject = (event.subject || '').toLowerCase();
            return subject.includes('flight') || 
                   subject.includes('travel') || 
                   subject.includes('trip') ||
                   subject.includes('vacation') ||
                   subject.includes('airport') ||
                   subject.includes('airline') ||
                   subject.includes('aa ') ||
                   subject.includes('delta') ||
                   subject.includes('united') ||
                   subject.includes('southwest') ||
                   subject.includes('dfw') ||
                   subject.includes('sna') ||
                   subject.includes('lax') ||
                   subject.includes('jfk') ||
                   subject.includes('ord');
          });
          
          // Convert calendar events to flight cards
          const flights = travelEvents.map(event => {
            const subject = event.subject || '';
            const eventDate = event.start?.dateTime ? new Date(event.start.dateTime) : new Date(event.date);
            
            // Parse flight details from subject
            const isReturn = subject.toLowerCase().includes('return');
            const airlineMatch = subject.match(/\b(AA|DL|UA|WN|American|Delta|United|Southwest)\b/i);
            const airline = airlineMatch ? airlineMatch[0].toUpperCase() : 'AA';
            
            // Parse airports from subject (e.g., "DFW --> SNA" or "DFW to SNA")
            const routeMatch = subject.match(/([A-Z]{3})\s*(?:-->|->|to|—)\s*([A-Z]{3})/i);
            const departure = routeMatch ? routeMatch[1].toUpperCase() : 'DFW';
            const arrival = routeMatch ? routeMatch[2].toUpperCase() : 'TBD';
            
            return {
              id: event.id || `flight-${Date.now()}-${Math.random()}`,
              type: isReturn ? 'return' : 'outbound',
              airline: airline === 'AA' ? 'American Airlines' : 
                       airline === 'DL' ? 'Delta Airlines' :
                       airline === 'UA' ? 'United Airlines' :
                       airline === 'WN' ? 'Southwest Airlines' : airline,
              airlineCode: airline,
              flightNumber: subject.match(/\b([A-Z]{2}\d{3,4})\b/)?.[1] || `${airline}`,
              departure: departure,
              arrival: arrival,
              date: eventDate,
              dateFormatted: eventDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              }),
              time: eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
              subject: subject,
              status: eventDate > new Date() ? 'upcoming' : 'completed'
            };
          });
          
          // Sort by date
          flights.sort((a, b) => a.date - b.date);
          
          // Filter out deleted flights from database
          let filteredFlights = flights;
          try {
            const deletedResponse = await fetch(`${API_URL}/api/flights/deleted`);
            if (deletedResponse.ok) {
              const deletedData = await deletedResponse.json();
              const deletedFlights = deletedData.deleted_flights || [];
              filteredFlights = flights.filter(flight => !deletedFlights.includes(flight.id));
            }
          } catch (err) {
            console.log('Could not fetch deleted flights from API, using all flights');
          }
          
          setFlightCards(filteredFlights);
          
          // Also load manually added flights from database
          try {
            const manualRes = await fetch(`${API_URL}/api/flights/manual`);
            if (manualRes.ok) {
              const manualData = await manualRes.json();
              const manualFlights = (manualData.flights || []).map(f => ({
                id: f.id,
                type: 'outbound',
                airline: f.airline,
                airlineCode: f.airline?.substring(0, 2)?.toUpperCase() || 'XX',
                flightNumber: f.flight_number || '',
                departure: f.departure,
                arrival: f.arrival,
                date: new Date(f.date),
                dateFormatted: new Date(f.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
                time: f.time || '',
                subject: f.notes || `${f.airline} ${f.departure} → ${f.arrival}`,
                status: f.status || 'upcoming',
                source: 'manual'
              }));
              filteredFlights = [...filteredFlights, ...manualFlights].sort((a, b) => a.date - b.date);
              setFlightCards(filteredFlights);
            }
          } catch (err) {
            console.log('Could not fetch manual flights');
          }
          
          // Also create trip cards from grouped flights (use filtered flights)
          const tripGroups = {};
          filteredFlights.forEach(flight => {
            const monthKey = `${flight.date.getFullYear()}-${flight.date.getMonth()}`;
            if (!tripGroups[monthKey]) {
              tripGroups[monthKey] = [];
            }
            tripGroups[monthKey].push(flight);
          });
          
          const tripCards = Object.entries(tripGroups).map(([key, groupFlights]) => ({
            id: key,
            destination: groupFlights[0]?.arrival || 'Trip',
            start_date: groupFlights[0]?.date.toISOString().split('T')[0],
            end_date: groupFlights[groupFlights.length - 1]?.date.toISOString().split('T')[0],
            flights: groupFlights,
            category: 'business',
            status: 'planned'
          }));
          
          // Filter out deleted trips from localStorage
          const deletedTrips = JSON.parse(localStorage.getItem('deleted_trips') || '[]');
          const filteredTrips = tripCards.filter(trip => !deletedTrips.includes(trip.id));
          
          setTrips(filteredTrips);
        }
      }
    } catch (error) {
      console.error('Error fetching travel events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create new trip (mockup mode)
      const newTrip = {
        id: `trip-${Date.now()}`,
        ...formData
      };
      setTrips([newTrip, ...trips]);
      setIsDialogOpen(false);
      setFormData({
        destination: '',
        start_date: '',
        end_date: '',
        purpose: '',
        status: 'planned',
        flight_details: '',
        hotel_details: '',
        notes: ''
      });
      alert('✅ Trip created successfully!');
    } catch (error) {
      console.error('Error creating trip:', error);
      alert('Failed to create trip. Please try again.');
    }
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setFormData({
      destination: trip.destination || '',
      start_date: trip.start_date || '',
      end_date: trip.end_date || '',
      purpose: trip.purpose || '',
      status: trip.status || 'planned',
      flight_details: trip.flight_details || '',
      hotel_details: trip.hotel_details || '',
      notes: trip.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteTrip = async (tripId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this trip?')) {
      try {
        // Delete trip
        const updatedTrips = trips.filter(trip => trip.id !== tripId);
        setTrips(updatedTrips);
        
        // Persist deletion to localStorage
        const deletedTrips = JSON.parse(localStorage.getItem('deleted_trips') || '[]');
        deletedTrips.push(tripId);
        localStorage.setItem('deleted_trips', JSON.stringify(deletedTrips));
        
        alert('✅ Trip deleted successfully!');
      } catch (error) {
        console.error('Error deleting trip:', error);
        alert('Failed to delete trip. Please try again.');
      }
    }
  };

  const handleViewDetails = (trip) => {
    setSelectedTrip(trip);
    setIsDetailsDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingTrip) return;

    try {
      // Update trip (mockup mode)
      const updatedTrips = trips.map(trip =>
        trip.id === editingTrip.id
          ? { ...trip, ...formData }
          : trip
      );
      setTrips(updatedTrips);
      setIsEditDialogOpen(false);
      setEditingTrip(null);
      setFormData({
        destination: '',
        start_date: '',
        end_date: '',
        purpose: '',
        status: 'planned',
        flight_details: '',
        hotel_details: '',
        notes: ''
      });
      alert('✅ Trip updated successfully!');
    } catch (error) {
      console.error('Error updating trip:', error);
      alert('Failed to update trip. Please try again.');
    }
  };

  // Delete a flight card
  const handleDeleteFlightCard = async (flightId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this flight?')) {
      try {
        // Save deletion to database for persistence
        const response = await fetch(`${API_URL}/api/flights/deleted`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ flight_id: flightId })
        });
        
        if (response.ok) {
          const updatedFlights = flightCards.filter(f => f.id !== flightId);
          setFlightCards(updatedFlights);
          console.log(`Flight ${flightId} deleted permanently`);
        } else {
          console.error('Failed to delete flight from database');
          alert('Failed to delete flight. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting flight:', error);
        alert('Failed to delete flight. Please try again.');
      }
    }
  };

  // Open boarding pass modal
  const openBoardingPass = async (flight) => {
    setSelectedFlight(flight);
    
    // Generate QR code with flight data
    const qrData = JSON.stringify({
      flightNumber: flight.flightNumber,
      airline: flight.airline,
      departure: flight.departure,
      arrival: flight.arrival,
      date: flight.dateFormatted,
      time: flight.time,
      passenger: 'PASSENGER NAME',
      seat: `${Math.floor(Math.random() * 30) + 1}${['A', 'B', 'C', 'D', 'E', 'F'][Math.floor(Math.random() * 6)]}`,
      gate: `${['A', 'B', 'C', 'D'][Math.floor(Math.random() * 4)]}${Math.floor(Math.random() * 30) + 1}`,
      boardingGroup: Math.floor(Math.random() * 5) + 1,
      confirmationCode: `${flight.airlineCode}${Math.random().toString(36).substring(2, 8).toUpperCase()}`
    });
    
    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
    
    setIsBoardingPassOpen(true);
  };

  const filteredTrips = filter === 'all' ? trips : trips.filter(t => t.category === filter);
  const filteredFlights = filter === 'all' ? flightCards : flightCards.filter(f => {
    // Map flight types to categories
    if (filter === 'business') return true; // All flights could be business
    if (filter === 'vacation') return f.subject?.toLowerCase().includes('vacation');
    if (filter === 'family') return f.subject?.toLowerCase().includes('family');
    if (filter === 'personal') return f.subject?.toLowerCase().includes('personal');
    return true;
  });

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Travel</h1>
          <p className="text-gray-400">Flight cards from your calendar events</p>
        </div>
        <Button 
          className="bg-red-500 hover:bg-red-600 text-white"
          onClick={() => setIsDialogOpen(true)}
        >
          <Plus size={18} className="mr-2" />
          Add Trip
        </Button>
      </div>

      {/* Category Filters */}
      <div className="mb-6 bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-4">
        <div className="flex flex-wrap gap-2 items-center">
          {categories.map((category) => {
            const count = category.id === 'all' ? flightCards.length : flightCards.filter(f => {
              if (category.id === 'business') return true;
              if (category.id === 'vacation') return f.subject?.toLowerCase().includes('vacation');
              if (category.id === 'family') return f.subject?.toLowerCase().includes('family');
              if (category.id === 'personal') return f.subject?.toLowerCase().includes('personal');
              return false;
            }).length;
            return (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                data-testid={`travel-category-${category.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === category.id
                    ? category.id === 'all' ? 'bg-white text-black' : `${category.color} text-white`
                    : 'bg-black text-gray-400 hover:text-white'
                }`}
              >
                <span>{category.icon}</span>
                {category.name} ({count})
              </button>
            );
          })}
          
          {/* Edit Categories Button */}
          <button
            onClick={() => setShowTravelCategoryModal(true)}
            data-testid="edit-travel-categories-btn"
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
            title="Edit Categories"
          >
            <Pencil size={14} />
            Edit
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-cyan-500 mb-2">{flightCards.length}</div>
          <div className="text-gray-400 text-sm">Total Trips</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-orange-500 mb-2">
            {flightCards.filter(f => f.status === 'upcoming').length}
          </div>
          <div className="text-gray-400 text-sm">Upcoming</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-green-500 mb-2">
            {flightCards.filter(f => f.status === 'completed').length}
          </div>
          <div className="text-gray-400 text-sm">Completed</div>
        </div>
        <div className="bg-transparent border border-white/60 rounded-2xl shadow-2xl shadow-[#C0C0C0]/10 p-6">
          <div className="text-4xl font-bold text-purple-500 mb-2">
            {flightCards.filter(f => f.status === 'cancelled').length || 0}
          </div>
          <div className="text-gray-400 text-sm">Cancelled</div>
        </div>
      </div>

      {/* Flight Cards from Calendar Events */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Plane size={24} className="text-blue-400" />
          Upcoming Flights
        </h2>
        
        {filteredFlights.length === 0 ? (
          <div className="bg-black border border-gray-600 rounded-xl p-8 text-center">
            <Plane size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 mb-2">No flight events found in your calendar</p>
            <p className="text-gray-500 text-sm">Add travel events to your Outlook calendar to see them here</p>
            <p className="text-gray-500 text-xs mt-2">Example: "AA Flight DFW to SNA" or "Return Flight - AA SNA to DFW"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFlights.map((flight) => (
              <div 
                key={flight.id}
                onClick={() => openBoardingPass(flight)}
                className={`bg-black border-2 rounded-xl p-5 hover:shadow-lg transition-all relative group cursor-pointer hover:scale-[1.02] ${
                  flight.type === 'return' ? 'border-orange-500' : 'border-blue-500'
                }`}
              >
                {/* Delete Button */}
                <button
                  onClick={(e) => handleDeleteFlightCard(flight.id, e)}
                  className="absolute top-3 right-3 p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  title="Delete flight"
                >
                  <Trash2 size={16} />
                </button>

                {/* Flight Type Badge */}
                <div className="flex items-center justify-between mb-3 pr-8">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    flight.type === 'return' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {flight.type === 'return' ? '↩ Return Flight' : '✈ Outbound Flight'}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    flight.status === 'upcoming' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {flight.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                  </span>
                </div>
                
                {/* Airline Info */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-black font-bold text-sm">{flight.airlineCode}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{flight.airline}</p>
                    <p className="text-gray-400 text-xs">{flight.flightNumber}</p>
                  </div>
                </div>
                
                {/* Route */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{flight.departure}</p>
                    <p className="text-xs text-gray-500">Departure</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <div className="h-0.5 w-12 bg-gradient-to-r from-blue-400 to-orange-400"></div>
                      <Plane size={16} className="text-white" />
                      <div className="h-0.5 w-12 bg-gradient-to-r from-orange-400 to-blue-400"></div>
                      <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{flight.arrival}</p>
                    <p className="text-xs text-gray-500">Arrival</p>
                  </div>
                </div>
                
                {/* Date & Time */}
                <div className="bg-gray-900 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-cyan-400" />
                    <span className="text-white text-sm font-medium">{flight.dateFormatted}</span>
                  </div>
                  {flight.time && (
                    <div className="flex items-center gap-1">
                      <Clock size={14} className="text-gray-400" />
                      <span className="text-gray-300 text-sm">{flight.time}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Flight Tracker Section */}
      <FlightTracker />

      {/* Add Trip Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Trip</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add travel information to your itinerary
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Destination *</label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., New York, NY"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">End Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Purpose *</label>
                <input
                  type="text"
                  required
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Purpose of travel"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                >
                  <option value="planned">Planned</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Flight Details</label>
                <input
                  type="text"
                  value={formData.flight_details}
                  onChange={(e) => setFormData({ ...formData, flight_details: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="e.g., AA 1234"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Hotel Details</label>
                <input
                  type="text"
                  value={formData.hotel_details}
                  onChange={(e) => setFormData({ ...formData, hotel_details: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Hotel name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-white"
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-white/60 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600 text-white">
                Add Trip
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Trip Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Trip</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Destination *</label>
                <input
                  type="text"
                  required
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Purpose *</label>
                <input
                  type="text"
                  required
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-300 mb-2 block">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                >
                  <option value="planned">Planned</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Flight Details</label>
                <textarea
                  value={formData.flight_details}
                  onChange={(e) => setFormData({ ...formData, flight_details: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Hotel Details</label>
                <textarea
                  value={formData.hotel_details}
                  onChange={(e) => setFormData({ ...formData, hotel_details: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-300 mb-2 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full bg-black border border-white/60 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-white/60 text-white hover:bg-white/5">
                Cancel
              </Button>
              <Button type="submit" className="bg-white hover:bg-cyan-600 text-white">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="bg-black border-white/60 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plane size={20} className="text-cyan-500" />
              Trip Details
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Complete travel information and flight details
            </DialogDescription>
          </DialogHeader>
          {selectedTrip && (
            <div className="space-y-6 py-4">
              {/* Trip Header */}
              <div className="bg-gradient-to-r from-gray-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-4">
                <h3 className="text-2xl font-bold text-white mb-2">{selectedTrip.destination}</h3>
                <p className="text-gray-300 text-sm mb-3">{selectedTrip.purpose}</p>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedTrip.status === 'upcoming' || selectedTrip.status === 'planned' ? 'bg-white/20 text-cyan-500' :
                    selectedTrip.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                    'bg-black text-gray-300'
                  }`}>
                    {selectedTrip.status}
                  </span>
                  {selectedTrip.category && (
                    <span className="text-xs text-gray-400">
                      Category: <span className="text-white font-semibold">{selectedTrip.category}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Travel Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-cyan-500" />
                    <p className="text-xs font-semibold text-gray-400 uppercase">Departure Date</p>
                  </div>
                  <p className="text-lg font-bold text-white">{selectedTrip.start_date}</p>
                </div>
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar size={16} className="text-purple-500" />
                    <p className="text-xs font-semibold text-gray-400 uppercase">Return Date</p>
                  </div>
                  <p className="text-lg font-bold text-white">{selectedTrip.end_date}</p>
                </div>
              </div>

              {/* Flight Details */}
              {selectedTrip.flight_details && (
                <div className="bg-transparent border border-cyan-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Plane size={16} className="text-cyan-500" />
                    <h4 className="text-sm font-bold text-white uppercase">Flight Details</h4>
                  </div>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-white whitespace-pre-wrap">{selectedTrip.flight_details}</p>
                  </div>
                </div>
              )}

              {/* Hotel Details */}
              {selectedTrip.hotel_details && (
                <div className="bg-transparent border border-purple-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={16} className="text-purple-500" />
                    <h4 className="text-sm font-bold text-white uppercase">Hotel Details</h4>
                  </div>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-white whitespace-pre-wrap">{selectedTrip.hotel_details}</p>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedTrip.notes && (
                <div className="backdrop-blur-sm bg-transparent border border-white/60 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-gray-400 uppercase mb-3">Additional Notes</h4>
                  <div className="bg-black rounded-lg p-3 border border-white/60">
                    <p className="text-gray-300 whitespace-pre-wrap">{selectedTrip.notes}</p>
                  </div>
                </div>
              )}

              {/* Empty State Messages */}
              {!selectedTrip.flight_details && !selectedTrip.hotel_details && !selectedTrip.notes && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No additional details available for this trip.</p>
                  <p className="text-gray-600 text-sm mt-2">Click Edit to add flight and hotel information.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDetailsDialogOpen(false)}
              className="border-white/60 text-white hover:bg-white/5"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                setIsDetailsDialogOpen(false);
                handleEditTrip(selectedTrip);
              }}
              className="bg-white hover:bg-cyan-600 text-white"
            >
              <Edit2 size={16} className="mr-2" />
              Edit Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boarding Pass Modal */}
      <Dialog open={isBoardingPassOpen} onOpenChange={setIsBoardingPassOpen}>
        <DialogContent className="bg-white text-black max-w-md p-0 overflow-hidden">
          {selectedFlight && (
            <div className="boarding-pass">
              {/* Header */}
              <div className={`p-4 text-white ${selectedFlight.type === 'return' ? 'bg-orange-500' : 'bg-blue-600'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-black font-bold text-lg">{selectedFlight.airlineCode}</span>
                    </div>
                    <div>
                      <p className="font-bold text-lg">{selectedFlight.airline}</p>
                      <p className="text-white/80 text-sm">{selectedFlight.flightNumber}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/70">BOARDING PASS</p>
                    <p className="font-bold">{selectedFlight.type === 'return' ? 'RETURN' : 'OUTBOUND'}</p>
                  </div>
                </div>
              </div>

              {/* Flight Route */}
              <div className="p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-gray-900">{selectedFlight.departure}</p>
                    <p className="text-sm text-gray-500 mt-1">Departure</p>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <div className="h-0.5 w-16 bg-gradient-to-r from-blue-500 to-orange-500"></div>
                      <Plane size={24} className="text-gray-700 transform rotate-90" />
                      <div className="h-0.5 w-16 bg-gradient-to-r from-orange-500 to-blue-500"></div>
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-gray-900">{selectedFlight.arrival}</p>
                    <p className="text-sm text-gray-500 mt-1">Arrival</p>
                  </div>
                </div>

                {/* Flight Details Grid */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Date</p>
                    <p className="font-bold text-gray-900">{selectedFlight.dateFormatted}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Time</p>
                    <p className="font-bold text-gray-900">{selectedFlight.time || 'TBD'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Gate</p>
                    <p className="font-bold text-gray-900">{`${['A', 'B', 'C', 'D'][Math.floor(Math.random() * 4)]}${Math.floor(Math.random() * 30) + 1}`}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Seat</p>
                    <p className="font-bold text-gray-900 text-xl">{`${Math.floor(Math.random() * 30) + 1}${['A', 'B', 'C', 'D', 'E', 'F'][Math.floor(Math.random() * 6)]}`}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Class</p>
                    <p className="font-bold text-gray-900">Economy</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-xs text-gray-500 uppercase">Group</p>
                    <p className="font-bold text-gray-900">{Math.floor(Math.random() * 5) + 1}</p>
                  </div>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="p-6 bg-white border-t-2 border-dashed border-gray-300">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 uppercase mb-1">Confirmation</p>
                    <p className="font-mono font-bold text-xl text-gray-900">{`${selectedFlight.airlineCode}${Math.random().toString(36).substring(2, 8).toUpperCase()}`}</p>
                    <p className="text-xs text-gray-400 mt-2">Scan QR code at security checkpoint</p>
                  </div>
                  <div className="flex flex-col items-center">
                    {qrCodeDataUrl ? (
                      <img src={qrCodeDataUrl} alt="Boarding Pass QR Code" className="w-32 h-32" />
                    ) : (
                      <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                        <QrCode size={48} className="text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-3 text-center text-white text-xs ${selectedFlight.type === 'return' ? 'bg-orange-500' : 'bg-blue-600'}`}>
                <p>Please arrive at the gate at least 30 minutes before departure</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Travel Category Management Modal */}
      {showTravelCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowTravelCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Travel Categories</h3>
              <button onClick={() => setShowTravelCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newTravelCategoryIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {travelCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewTravelCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newTravelCategoryName} onChange={(e) => setNewTravelCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTravelCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-travel-category-input" />
              </div>
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {travelCategoryColors.map(color => (<button key={color} onClick={() => setNewTravelCategoryColor(color)} className={`w-6 h-6 rounded-full ${color} ${newTravelCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`} />))}
                </div>
              </div>
              <button onClick={addTravelCategory} disabled={!newTravelCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-travel-category-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customTravelCategories.filter(c => c.id !== 'all').map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingTravelCategory === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editTravelCategoryValue} onChange={(e) => setEditTravelCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateTravelCategory(cat.id, editTravelCategoryValue); if (e.key === 'Escape') { setEditingTravelCategory(null); setEditTravelCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateTravelCategory(cat.id, editTravelCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingTravelCategory(null); setEditTravelCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm`}>{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['business', 'personal'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingTravelCategory(cat.id); setEditTravelCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['business', 'personal'].includes(cat.id) && <button onClick={() => deleteTravelCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowTravelCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-travel-category-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Travel;